import Aria from './aria.helper.js';
import Inert from './inert.helper.js';
import {cfg} from '../plugins/cs-main-navigation.config.js';

export default class MenuHelper {

  /**
   * Returns all sibling elements of a given DOM element.
   *
   * @param {HTMLElement} el - The reference element.
   * @returns {HTMLElement[]} Array of sibling elements.
   */
  static getSiblings(el) {
    return Array.from(el.parentElement.children).filter(child => child !== el);
  }

  /**
   * Returns the subnavigation layer (e.g., submenu) for a given link element.
   *
   * @param {HTMLElement} link - The link element.
   * @returns {HTMLElement|null} The next sibling element (submenu), or `null` if none exists.
   */
  static getSubNavigationLayerFromLink(link) {
    return link.nextElementSibling;
  }

  /**
   * Returns the parent navigation layer of a given link.
   *
   * @param {HTMLElement} link - The link element inside a menu.
   * @param {boolean} [useFlyoutOuterContainer=false] - Whether to search for the flyout outer container, otherwise flyout inner container.
   * @param {number|null} [level=null] - Optional override for menu level, e.g. if link does not have the data attribute for level.
   * @returns {HTMLElement|null} The parent navigation container element.
   */
  static getParentNavigationLayerFromLink(link, useFlyoutOuterContainer = false, level = null) {
    let parentNavigationLayer = null;
    let levelValue = level ? level : Number(link.getAttribute(cfg.dataAttrs.menuLevel));

    if (levelValue == 1) {
      parentNavigationLayer = link.closest(cfg.selectors.menuMain);
    } else if (levelValue == 2) {
      parentNavigationLayer = link.closest(useFlyoutOuterContainer ? cfg.selectors.flyoutContainer : cfg.selectors.flyoutInner);
    } else {
      parentNavigationLayer = link.closest(cfg.selectors.menuList);
    }
    return parentNavigationLayer;
  }

  /**
   * Gets the trigger link (e.g., the menu item that opened a submenu) for a given submenu link.
   *
   * @param {HTMLElement} subLink - A link inside a submenu.
   * @returns {HTMLElement|null} The trigger link element.
   */
  static getParentTriggerLink(subLink) {
    let parentNavigationLayer = this.getParentNavigationLayerFromLink(subLink, true);
    return parentNavigationLayer.previousElementSibling;
  }

  /**
   * Resets all expanded ARIA states for submenus starting from a given level.
   *
   * @param {HTMLElement} flyoutContainer - The container element of the flyout.
   * @param {number} startingLevel - The starting menu level.
   */
  static resetExpandedSubLevels(flyoutContainer, startingLevel) {
    let level = startingLevel;

    while (true) {
      const selector = `${cfg.selectors.menuLinkWithChildren}[aria-expanded="true"][${cfg.dataAttrs.menuLevel}="${level}"]`;
      const expandedLinks = flyoutContainer.querySelectorAll(selector);

      if (expandedLinks.length === 0) {
        break;
      }

      expandedLinks.forEach(link => {
        const subNavigationLayer = this.getSubNavigationLayerFromLink(link);
        Aria.setExpanded(link, false);
        Inert.setInertAttr(subNavigationLayer);
      });

      level++;
    }
  }

  /**
   * Returns the currently active menu link based on a data attribute in the global wrapper.
   *
   * @returns {HTMLElement|null} The active menu link element, or `null` if not found.
   */
  static getActiveMenuLink() {
    const wrapper = document.querySelector(cfg.selectors.globalWrapper);
    if (!wrapper) return null;

    const activeUid = wrapper.getAttribute(cfg.dataAttrs.activeLinkUid);
    if (!activeUid) return null;

    return document.querySelector(`${cfg.selectors.menuLink}[${cfg.dataAttrs.linkItemUid}="${activeUid}"]`);
  }

  /**
   * Toggles the `inert` attribute for elements in a mobile menu level to manage focus and accessibility.
   *
   * Affects the clicked link, its sibling menu items, and the associated list header. Additionally,
   * it inverts the `inert` state for the corresponding subnavigation layer.
   *
   * @param {HTMLElement} link - The menu link element that was interacted with.
   * @param {boolean} [activate=true] - Whether to activate (`true`) or deactivate (`false`) the `inert` state on surrounding elements.
   */
  static toggleInertLevelMobile(link, activate = true) {
    const parentItem = link.closest(cfg.selectors.menuItem);
    const parentItemSiblings = MenuHelper.getSiblings(parentItem);
    const parentList = link.closest(cfg.selectors.menuList);
    const parentListHeader = parentList.previousElementSibling;

    // add or remove inert for:
    // clicked link
    // list item siblings
    // list header
    const inertTargets = [
      link,
      ...parentItemSiblings,
      ...(parentListHeader?.matches(cfg.selectors.menuListHeader) ? [parentListHeader] : [])
    ];

    Inert.setInertAttrOnElements(inertTargets, activate);

    let subNavigationLayer = MenuHelper.getSubNavigationLayerFromLink(link);
    Inert.setInertAttr(subNavigationLayer, !activate);
  }
}
